import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { inject, Injectable } from '@angular/core';
import { CustomRxState } from '@core/state/rx.state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { combineLatest, Observable } from 'rxjs';
import { TariffVersion } from '@api/models/TariffEditorApi/Contract/tariff-version';
import { TariffService } from '@api/services/tariff.service';
import { SessionStorage, SessionStorageService } from 'ngx-webstorage';
import { StorageKeys } from '@core/storage/storage.keys';
import { TariffVersionBody } from '@api/models/TariffEditorApi/Contract/tariff-version-body';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { ListFareMenuResponse } from '@api/models/TariffEditorApi/Contract/list-fare-menu-response';
import {
    PopupSortProductListState,
} from '../../features/tariff-versions/popup-sort-product-list/popup-sort-product-list.component';
import { SetFareSortRequest } from '@api/models/TariffEditorApi/Contract/set-fare-sort-request';
import { AddFarePassRequest } from '@api/models/TariffEditorApi/Contract/add-fare-pass-request';
import { AddFarePassResponse } from '@api/models/TariffEditorApi/Contract/add-fare-pass-response';
import { SetFarePassRequest } from '@api/models/TariffEditorApi/Contract/set-fare-pass-request';
import { isNil, isNumber } from 'lodash-es';
import { FarePassDataRequest } from '@api/models/TariffEditorApi/Contract/fare-pass-data-request';
import { FarePassData } from '@api/models/TariffEditorApi/Contract/fare-pass-data';
import { Service } from '@api/models/TariffEditorApi/Contract/service';
import { PeriodExclude } from '@api/models/TariffEditorApi/Contract/period-exclude';
import { PeriodInclude } from '@api/models/TariffEditorApi/Contract/period-include';
import { TariffCondition } from '@api/models/TariffEditorApi/Contract/tariff-condition';
import { PrivService } from '@api/services/priv.service';
import { Priv } from '@api/models/ClientSupportApi/Contract/priv';
import { RouteForRoute } from '@api/models/BoLanka/Contract/route-for-route';
import { FareTicketDataRequest } from '@api/models/TariffEditorApi/Contract/fare-ticket-data-request';
import { FareTicketData } from '@api/models/TariffEditorApi/Contract/fare-ticket-data';
import { AddFareTicketRequest } from '@api/models/TariffEditorApi/Contract/add-fare-ticket-request';
import { AddFareTicketResponse } from '@api/models/TariffEditorApi/Contract/add-fare-ticket-response';
import { VisOrg } from '@api/models/UserService/Contract/vis-org';
import { VisOrgService } from '@api/services/vis-org.service';
import { ListFarePriceTypeResponse } from '@api/models/TariffEditorApi/Contract/list-fare-price-type-response';
import { TariffTypes, TariffTypesForPriceType } from '../models/tariff-types';
import { ListTariffZoneResponse } from '@api/models/TariffEditorApi/Contract/list-tariff-zone-response';
import { SetFareTicketRequest } from '@api/models/TariffEditorApi/Contract/set-fare-ticket-request';
import { ListFareLimitResponse } from '@api/models/TariffEditorApi/Contract/list-fare-limit-response';
import { SetFareLimitRequest } from '@api/models/TariffEditorApi/Contract/set-fare-limit-request';
import { ListPriceFsResponse } from '@api/models/TariffEditorApi/Contract/list-price-fs-response';
import { SetPriceFsRequest } from '@api/models/TariffEditorApi/Contract/set-price-fs-request';
import { ListUpchargeResponse } from '@api/models/TariffEditorApi/Contract/list-upcharge-response';
import { DesignElementsService, UpchargeService } from '@api/services';
import { SetUpchargeRequest } from '@api/models/TariffEditorApi/Contract/set-upcharge-request';
import { ListUpchargeConditionResponse } from '@api/models/TariffEditorApi/Contract/list-upcharge-condition-response';
import { ListFarePrvPartResponse } from '@api/models/TariffEditorApi/Contract/list-fare-prv-part-response';
import { SetFarePrvPartRequest } from '@api/models/TariffEditorApi/Contract/set-fare-prv-part-request';
import { Zones } from '@api/models/TariffEditorApi/Contract/zones';
import { PriceZone } from '@api/models/TariffEditorApi/Contract/price-zone';
import { PriceZoneARequest } from '@api/models/TariffEditorApi/Contract/price-zone-a-request';
import { SetFareLabelRequest } from '@api/models/TariffEditorApi/Contract/set-fare-label-request';
import { VisOrgFiltered } from '@api/models/UserService/Contract/vis-org-filtered';
import { ApElementActListResponse } from '@api/models/BannerApi/Contract/ap-element-act-list-response';
import { Domain } from '@api/models/TariffEditorApi/Contract/domain';
import { TariffPass } from '@api/models/TariffEditorApi/Contract/tariff-pass';
import { TariffTicket } from '@api/models/TariffEditorApi/Contract/tariff-ticket';
import { TariffCap } from '@api/models/TariffEditorApi/Contract/tariff-cap';
import { TariffDomain } from '@api/models/TariffEditorApi/Contract/tariff-domain';
import { TariffPassBody } from '@api/models/TariffEditorApi/Contract/tariff-pass-body';
import { TariffCapBody } from '@api/models/TariffEditorApi/Contract/tariff-cap-body';
import { TariffDomainBody } from '@api/models/TariffEditorApi/Contract/tariff-domain-body';


interface ITariffsState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    tariffVersions: TariffVersion[];
    transportManagers: VisOrg[];
    transportManagerId: number;
    tariffMenu: ListFareMenuResponse[];
    selectedTariffVersion: string;
    transportManagerServices: Service[];
    selectedTransportManagerService: number;
    tariffPeriodExcludeGroup: PeriodExclude[];
    tariffPeriodIncludeGroup: PeriodInclude[];
    tariffConditions: TariffCondition[];
    tariffPrivileges: Priv[];
    tariffRoutes: RouteForRoute[];
    tariffUpChargeConditions: ListUpchargeConditionResponse[];
    priceTypesFullList: ListFarePriceTypeResponse[];
    designElements: ApElementActListResponse[];
    domainsControl: Domain[];
    passes: TariffPass[];
    tickets: TariffTicket[];
    caps: TariffCap[];
    domainsThreshold: TariffDomain[];
}

interface TariffsActions {
    requestTariffVersions: TariffVersion[];
    setTransportManagerId: number;
    requestTariffMenu: ListFareMenuResponse[];
    requestTransportManagerServices: Service[];
    setSelectedTariffVersion: string;
    setSelectedTransportManagerService: number;
    requestPasses: TariffPass[];
    requestTickets: TariffTicket[];
    requestCaps: TariffCap[];
    requestDomainsThreshold: TariffDomain[];
}

@Injectable()
export class TariffsState extends CustomRxState<ITariffsState> {
    @SessionStorage(StorageKeys.RefSelectedTrm)
    public trmId: number;

    @SessionStorage(StorageKeys.RefSelectedTariffService)
    public srvId: number;

    readonly #actions: RxActions<TariffsActions> = new RxActionFactory<TariffsActions>().create();
    readonly #tariffService: TariffService = inject(TariffService);
    readonly #upchargeService: UpchargeService = inject(UpchargeService);
    readonly #privilegeService: PrivService = inject(PrivService);
    readonly #designElementsService: DesignElementsService = inject(DesignElementsService);
    readonly #visOrgService: VisOrgService = inject(VisOrgService);
    readonly #sessionStorageService: SessionStorageService = inject(SessionStorageService);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly tariffVersions$: Observable<TariffVersion[]> = this.select('tariffVersions');
    public readonly transportManagerId$: Observable<number> = this.select('transportManagerId');
    public readonly tariffMenu$: Observable<ListFareMenuResponse[]> = this.select('tariffMenu');
    public readonly transportManagerServices$: Observable<Service[]> = this.select('transportManagerServices');
    public readonly selectedTariffVersion$: Observable<string> = this.select('selectedTariffVersion');
    public readonly selectedTransportManagerService$: Observable<number> = this.select('selectedTransportManagerService');
    public readonly tariffPeriodExcludeGroup$: Observable<PeriodExclude[]> = this.selectLazy(
        'tariffPeriodExcludeGroup',
        this.requestPeriodExclude,
    );

    public readonly tariffPeriodIncludeGroup$: Observable<PeriodInclude[]> = this.selectLazy(
        'tariffPeriodIncludeGroup',
        this.requestPeriodInclude,
    );

    public readonly tariffConditions$: Observable<TariffCondition[]> = this.selectLazy('tariffConditions', this.requestTariffConditions);
    public readonly tariffPrivileges$: Observable<Priv[]> = this.selectLazy('tariffPrivileges', this.requestTariffPrivileges);
    public readonly designElements$: Observable<ApElementActListResponse[]> = this.selectLazy('designElements', this.requestDesignElements);
    public readonly domainsControl$: Observable<Domain[]> = this.selectLazy('domainsControl', this.requestDomainsControl);

    public readonly priceTypesFullList$: Observable<ListFarePriceTypeResponse[]> = this.selectLazy(
        'priceTypesFullList',
        this.requestPriceTypeFullList,
    );

    public readonly tariffUpChargeConditions$: Observable<ListUpchargeConditionResponse[]> = this.selectLazy(
        'tariffUpChargeConditions',
        this.requestTariffUpChargeConditions,
    );

    public readonly transportManagers$: Observable<VisOrg[]> = this.selectLazy('transportManagers', this.requestTransportManagers).pipe(
        filter(Boolean),
        tap((transportManagers: VisOrg[]) => {
            if (transportManagers?.length) {
                const orgTrm: VisOrg = transportManagers.find((org: VisOrgFiltered) => isNumber(org.trmId));

                if (isNil(this.trmId) && orgTrm) {
                    this.trmId = orgTrm.trmId;
                }
            } else {
                this.trmId = null;
            }
        }),
    );

    public readonly passes$: Observable<TariffPass[]> = this.select('passes');
    public readonly tickets$: Observable<TariffTicket[]> = this.select('tickets');
    public readonly caps$: Observable<TariffCap[]> = this.select('caps');
    public readonly domainsThreshold$: Observable<TariffDomain[]> = this.select('domainsThreshold');


    constructor() {
        super();

        this.connectSelectors();
        this.setDefaultState();

        this.requestTariffVersions();

        this.hold(
            combineLatest([
                this.selectedTariffVersion$.pipe(filter(Boolean), distinctUntilChanged()),
                this.transportManagerId$.pipe(filter(Boolean), distinctUntilChanged()),
            ]),
            ([version, trmId]: [string, number]) => {
                this.requestTransportManagerServices(version, trmId);
            },
        );

        this.hold(
            this.#sessionStorageService.observe(StorageKeys.RefSelectedTrm).pipe(filter((trmId: number) => isNumber(trmId))),
            this.#actions.setTransportManagerId,
        );
    }

    public requestTariffVersions(): void {
        this.hold(
            this.transportManagerId$.pipe(
                filter(isNumber),
                switchMap((trmId: number) => this.#tariffService.apiTariffVersionsGet({ trmId })),
            ),
            this.#actions.requestTariffVersions,
        );
    }

    public getTariffVersionById(versionId: string): Observable<TariffVersion> {
        return this.tariffVersions$.pipe(
            map((versions: TariffVersion[]) => versions?.find((v: TariffVersion) => v.dateFrom === versionId)),
        );
    }

    public setTransportManagerId(trmId: number): void {
        this.#actions.setTransportManagerId(trmId);
    }

    public setSelectedTariffVersion(version: string): void {
        this.#actions.setSelectedTariffVersion(version);
    }

    public setSelectedTransportManagerService(srvId: number): void {
        this.#actions.setSelectedTransportManagerService(srvId);
    }

    public createTariffVersions(body: TariffVersionBody): Observable<TariffVersion> {
        return this.transportManagerId$.pipe(
            switchMap((trmId: number) =>
                this.#tariffService.apiTariffVersionsPost({ trmId, body }).pipe(
                    startWithLoading(this, StartWithUnit.CreateTariffVersion),
                    tap(() => this.requestTariffVersions()),
                ),
            ),
        );
    }

    public deleteTariffVersions(dateFrom: string): Observable<void> {
        return this.transportManagerId$.pipe(
            switchMap((trmId: number) =>
                this.#tariffService.apiTariffVersionsDeleteGet({ trmId, dateFrom }).pipe(
                    startWithLoading(this, StartWithUnit.DeleteTariffVersion),
                    tap(() => this.requestTariffVersions()),
                ),
            ),
        );
    }

    public requestTariffMenu(srvId: number, trfType: PopupSortProductListState): void {
        this.hold(
            this.transportManagerId$.pipe(
                switchMap((trmId: number) => this.#tariffService.apiTariffListFareMenuGet({ trmId, trfType, srvId })),
            ),
            this.#actions.requestTariffMenu,
        );
    }

    public setSortTariffMenu(body: SetFareSortRequest): Observable<void> {
        return this.#tariffService.apiTariffSetFareSortPost({ body }).pipe(startWithLoading(this, StartWithUnit.SetSortTariffMenu));
    }

    public addTariffPass(body: AddFarePassRequest): Observable<AddFarePassResponse> {
        return this.#tariffService.apiTariffAddFarePassPost({ body }).pipe(startWithLoading(this, StartWithUnit.AddOrUpdateTariffPass));
    }

    public addTariffTicket(body: AddFareTicketRequest): Observable<AddFareTicketResponse> {
        return this.#tariffService.apiTariffAddFareTicketPost({ body }).pipe(startWithLoading(this, StartWithUnit.AddOrUpdateTariffTicket));
    }

    public getTariffPassById(body: FarePassDataRequest): Observable<FarePassData> {
        return this.#tariffService.apiTariffGetFarePassDataPost({ body });
    }

    public getTariffTicketById(body: FareTicketDataRequest): Observable<FareTicketData> {
        return this.#tariffService.apiTariffGetFareTicketDataPost({ body });
    }

    public updateTariffPass(body: SetFarePassRequest): Observable<void> {
        return this.#tariffService.apiTariffSetFarePassPost({ body }).pipe(startWithLoading(this, StartWithUnit.AddOrUpdateTariffPass));
    }

    public updateFareLabel(body: SetFareLabelRequest): Observable<void> {
        return this.#tariffService.apiTariffSetFareLabelPost({ body }).pipe(startWithLoading(this, StartWithUnit.AddOrUpdateTariffPass));
    }

    public updateTariffTicket(body: SetFareTicketRequest): Observable<FareTicketData> {
        return this.#tariffService.apiTariffSetFareTicketPost({ body }).pipe(startWithLoading(this, StartWithUnit.AddOrUpdateTariffTicket));
    }

    public deleteTariff(trfId: number, date: string, code: TariffTypes): Observable<void> {
        return this.#tariffService.apiTariffRemoveFareDestroyDelete({ trfId, date }).pipe(
            startWithLoading(this, StartWithUnit.DeleteTariff),
            tap(() => {
                if (code === TariffTypes.Pass) {
                    this.requestPasses(date);
                }

                if (code === TariffTypes.Ticket) {
                    this.requestTickets(date);
                }
            }),
        );
    }

    public requestTransportManagerServices(dateFrom: string, trmId?: number): void {
        this.hold(
            this.#tariffService.apiTariffServicesGet({ dateFrom, trmId }).pipe(
                startWithLoading(this, StartWithUnit.RequestTransportManagerServices),
                filter(Boolean),
                tap((services: Service[]) => {
                    if (services?.length) {
                        const serviceTrm: Service = services[0];

                        if (isNil(this.srvId) && serviceTrm) {
                            this.#actions.setSelectedTransportManagerService(serviceTrm.id);
                        } else if (!isNil(this.srvId)) {
                            const existService: Service = services.find((s: Service) => s.id === this.srvId);

                            if (!existService && serviceTrm) {
                                this.#actions.setSelectedTransportManagerService(serviceTrm.id);
                            }
                        }
                    } else {
                        this.#actions.setSelectedTransportManagerService(null);
                    }
                }),
            ),
            this.#actions.requestTransportManagerServices,
        );
    }

    public getTariffVersionDataById(tariffVersionDate: string): Observable<TariffVersion> {
        return this.tariffVersions$.pipe(
            map((versions: TariffVersion[]) => versions?.find((version: TariffVersion) => version.dateFrom === tariffVersionDate)),
        );
    }

    public getTariffPriceTypes(trfType: TariffTypesForPriceType): Observable<ListFarePriceTypeResponse[]> {
        return this.selectedTransportManagerService$.pipe(
            switchMap((srvId: number) => this.#tariffService.apiTariffListFarePriceTypeGet({ srvId, trfType })),
        );
    }

    public getTariffZones(): Observable<ListTariffZoneResponse[]> {
        return this.selectedTransportManagerService$.pipe(
            switchMap((srvId: number) => this.#tariffService.apiTariffListTariffZoneGet({ srvId })),
        );
    }

    public getServicesForRelocation(dateFrom: string): Observable<Service[]> {
        return combineLatest([this.transportManagerId$, this.selectedTransportManagerService$]).pipe(
            switchMap(([trmId, basicSrvId]: [number, number]) =>
                this.#tariffService.apiTariffServicesGet({
                    trmId,
                    dateFrom,
                    basicSrvId,
                }),
            ),
            map((items: Service[]) => items?.filter((item: Service) => item.isCplx)),
        );
    }

    public getTariffFareLimits(date: string, trfId: number): Observable<ListFareLimitResponse[]> {
        return this.selectedTransportManagerService$.pipe(
            switchMap((srvId: number) =>
                this.#tariffService.apiTariffListFareLimitGet({
                    date,
                    srvId,
                    trfId,
                }),
            ),
        );
    }

    public setFareLimits(body: SetFareLimitRequest): Observable<void> {
        return this.#tariffService.apiTariffSetFareLimitPost({ body }).pipe(startWithLoading(this, StartWithUnit.SetFareLimits));
    }

    public getTariffFareStages(trfId: number): Observable<ListPriceFsResponse[]> {
        return combineLatest([this.selectedTransportManagerService$, this.selectedTariffVersion$]).pipe(
            switchMap(([srvId, date]: [number, string]) =>
                this.#tariffService.apiTariffListPriceFsGet({
                    date,
                    srvId,
                    trfId,
                }),
            ),
        );
    }

    public setFareStages(body: SetPriceFsRequest): Observable<void> {
        return this.#tariffService.apiTariffSetPriceFsPost({ body }).pipe(startWithLoading(this, StartWithUnit.SetFareStages));
    }

    public getTariffUpChargePrices(srvIdCplx: number, trfId: number, ucId: number): Observable<ListUpchargeResponse[]> {
        return this.selectedTariffVersion$.pipe(
            switchMap((date: string) =>
                this.#upchargeService.apiTariffListUpchargeGet({
                    date,
                    trfId,
                    srvIdCplx,
                    ucId,
                }),
            ),
        );
    }

    public setTariffUpChargePrices(body: SetUpchargeRequest): Observable<void> {
        return this.#upchargeService.apiTariffSetUpchargePost({ body }).pipe(startWithLoading(this, StartWithUnit.SetUpCharges));
    }

    public getTariffPrvPasses(trfId: number): Observable<ListFarePrvPartResponse[]> {
        return combineLatest([this.selectedTransportManagerService$, this.selectedTariffVersion$]).pipe(
            switchMap(([srvId, date]: [number, string]) =>
                this.#tariffService.apiTariffListFarePrvPartGet({
                    date,
                    srvId,
                    trfId,
                }),
            ),
        );
    }

    public setTariffPrvPasses(body: SetFarePrvPartRequest): Observable<void> {
        return this.#tariffService.apiTariffSetFarePrvPartPost({ body }).pipe(startWithLoading(this, StartWithUnit.SetPrvPasses));
    }

    public getTariffZonesForPriceTable(): Observable<Zones[]> {
        return this.selectedTransportManagerService$.pipe(
            switchMap((srvId: number) =>
                this.#tariffService.apiTariffListZonesGet({
                    srvId,
                }),
            ),
        );
    }

    public getTariffZonesPrices(trfId: number): Observable<PriceZone[]> {
        return combineLatest([this.selectedTransportManagerService$, this.selectedTariffVersion$]).pipe(
            switchMap(([srvId, date]: [number, string]) =>
                this.#tariffService.apiTariffListPriceZonePost({
                    body: {
                        srvId,
                        date,
                        trfId,
                    },
                }),
            ),
        );
    }

    public setTariffZonesPrices(body: PriceZoneARequest): Observable<void> {
        return this.#tariffService.apiTariffSetPriceZonePost({ body }).pipe(startWithLoading(this, StartWithUnit.SetZonesPrices));
    }

    public requestPasses(dateFrom: string): void {
        this.hold(
            this.transportManagerId$.pipe(
                switchMap((trmId: number) => this.#tariffService.apiTariffTariffsPassGet({
                    trmId,
                    dateFrom,
                })),
            ),
            this.#actions.requestPasses,
        );
    }

    public requestTickets(dateFrom: string): void {
        this.hold(
            this.transportManagerId$.pipe(
                switchMap((trmId: number) => this.#tariffService.apiTariffTariffsTicketGet({
                    trmId,
                    dateFrom,
                })),
            ),
            this.#actions.requestTickets,
        );
    }

    public requestCaps(dateFrom: string): void {
        this.hold(
            this.transportManagerId$.pipe(
                switchMap((trmId: number) => this.#tariffService.apiTariffTariffsCapGet({
                    trmId,
                    dateFrom,
                })),
            ),
            this.#actions.requestCaps,
        );
    }

    public requestDomainsThreshold(dateFrom: string): void {
        this.hold(
            this.transportManagerId$.pipe(
                switchMap((trmId: number) => this.#tariffService.apiTariffTariffsDomainThresholdGet({
                    trmId,
                    dateFrom,
                })),
            ),
            this.#actions.requestDomainsThreshold,
        );
    }

    public savePass(body: TariffPassBody[], bodyForDisable: number[]): Observable<[TariffPass[], void]> {
        return combineLatest([
            this.#tariffService.apiTariffTariffsPassPut({ trmId: this.get('transportManagerId'), body }),
            this.#tariffService.apiTariffTariffsPassDisableGet({
                trmId: this.get('transportManagerId'),
                tariffIds: bodyForDisable,
                dateFrom: this.get('selectedTariffVersion'),
            }),
        ]).pipe(
            startWithLoading(this, StartWithUnit.SaveTariffPass),
            tap(
                () => this.requestPasses(this.get('selectedTariffVersion')),
            ),
        );
    }

    public saveTickets(body: TariffPassBody[], bodyForDisable: number[]): Observable<[TariffTicket[], void]> {
        return combineLatest([
            this.#tariffService.apiTariffTariffsTicketPut({ trmId: this.get('transportManagerId'), body }),
            this.#tariffService.apiTariffTariffsTicketDisableGet({
                trmId: this.get('transportManagerId'),
                tariffIds: bodyForDisable,
                dateFrom: this.get('selectedTariffVersion'),
            }),
        ]).pipe(
            startWithLoading(this, StartWithUnit.SaveTariffTickets),
            tap(
                () => this.requestTickets(this.get('selectedTariffVersion')),
            ),
        );
    }

    public saveCaps(body: TariffCapBody[], bodyForDisable: number[]): Observable<[void, void]> {
        return combineLatest([
            this.#tariffService.apiTariffTariffsCapPut({ trmId: this.get('transportManagerId'), body }),
            this.#tariffService.apiTariffTariffsCapDisableGet({
                trmId: this.get('transportManagerId'),
                tariffIds: bodyForDisable,
                dateFrom: this.get('selectedTariffVersion'),
            }),
        ]).pipe(
            startWithLoading(this, StartWithUnit.SaveCaps),
            tap(
                () => this.requestCaps(this.get('selectedTariffVersion')),
            ),
        );
    }

    public saveDomains(body: TariffDomainBody[]): Observable<void> {
        return this.#tariffService.apiTariffTariffsDomainPut({ trmId: this.get('transportManagerId'), body }).pipe(
            startWithLoading(this, StartWithUnit.SaveDomains),
            tap(
                () => this.requestDomainsThreshold(this.get('selectedTariffVersion')),
            ),
        );
    }


    private requestPeriodExclude(): Observable<PeriodExclude[]> {
        return this.#tariffService.apiTariffListPeriodExcludeGet();
    }

    private requestPeriodInclude(): Observable<PeriodInclude[]> {
        return this.#tariffService.apiTariffListPeriodIncludeGet();
    }

    private requestTariffConditions(): Observable<TariffCondition[]> {
        return this.#tariffService.apiTariffListTariffConditionGet();
    }

    private requestTariffPrivileges(): Observable<Priv[]> {
        return this.#privilegeService.apiSupportListPrivCategGet();
    }

    private requestPriceTypeFullList(): Observable<ListFarePriceTypeResponse[]> {
        return this.#tariffService.apiTariffListFarePriceTypeGet();
    }

    private requestTariffUpChargeConditions(): Observable<ListUpchargeConditionResponse[]> {
        return this.#upchargeService.apiTariffListUpchargeConditionGet();
    }

    private requestDesignElements(): Observable<ApElementActListResponse[]> {
        return this.#designElementsService.apiBannerApElementActListGet();
    }

    private requestDomainsControl(): Observable<Domain[]> {
        return this.transportManagerId$.pipe(
            switchMap((trmId: number) => this.#tariffService.apiTariffTariffsDomainGet({ trmId })),
        );
    }

    private requestTransportManagers(): Observable<VisOrg[]> {
        return this.#visOrgService
            .apiUsersListVisOrgGet()
            .pipe(map((items: VisOrg[]) => items.filter((item: VisOrg) => isNumber(item.trmId))));
    }

    private connectSelectors(): void {
        this.connect('transportManagerServices', this.#actions.requestTransportManagerServices$);
        this.connect('selectedTariffVersion', this.#actions.setSelectedTariffVersion$);
        this.connect('tariffVersions', this.#actions.requestTariffVersions$);
        this.connect('tariffMenu', this.#actions.requestTariffMenu$);
        this.connect('transportManagerId', this.#actions.setTransportManagerId$.pipe(tap((trmId: number) => (this.trmId = trmId))));

        this.connect(
            'selectedTransportManagerService',
            this.#actions.setSelectedTransportManagerService$.pipe(tap((srvId: number) => (this.srvId = srvId))),
        );

        this.connect('passes', this.#actions.requestPasses$);
        this.connect('tickets', this.#actions.requestTickets$);
        this.connect('caps', this.#actions.requestCaps$);
        this.connect('domainsThreshold', this.#actions.requestDomainsThreshold$);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            tariffVersions: null,
            transportManagers: null,
            selectedTariffVersion: null,
            transportManagerServices: null,
            passes: null,
            caps: null,
            tickets: null,
            domainsThreshold: null,
            tariffConditions: null,
            tariffPeriodIncludeGroup: null,
            tariffPeriodExcludeGroup: null,
            selectedTransportManagerService: isNumber(this.srvId) ? Number(this.srvId) : null,
            transportManagerId: isNumber(this.trmId) ? Number(this.trmId) : null,
        });
    }
}
